/**
 * BookingPageContent Component
 *
 * Shared component for rendering the booking page content.
 * Used by both:
 * - BookingPageContainer (actual booking page)
 * - BookingPagePreview (preview in meeting type settings)
 *
 * This ensures 100% UI accuracy between preview and production.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBookingPage } from "../BookingPageContext";
import { useBookingData } from "../../../hooks/useBookingData";
import BookingPageHeader from "./BookingPageHeader";
import BookingFlow from "./BookingFlow";
import ServiceBookingFlow from "./BookingFlow/ServiceBookingFlow";
import BookingConfirmation from "./BookingConfirmation";
import PaymentScreen from "./PaymentScreen";
import CancelFlow from "./CancelFlow";
import LanguageSelector from "../../../components/LanguageSelector";
import { applyBrandColor, removeBrandColor } from "../../../utils/brandColorTheme";
import formTemplateService from "../../../services/formTemplateService";
import { BookingActionContext } from "../../../services/bookingActionService";
import { MeetingType } from "../../../services/meetingTypeService";
import { PublicProduct, PublicProvider, ServiceBookingSelection } from "../../../types/product";

export interface BookingPageContentProps {
  /**
   * Username for fetching meeting type (not needed for preview mode)
   */
  username?: string;

  /**
   * Slug for fetching meeting type (not needed for preview mode)
   */
  slug?: string;

  /**
   * Mode: 'booking' | 'reschedule' | 'cancel' | 'preview'
   */
  mode?: 'booking' | 'reschedule' | 'cancel' | 'preview';

  /**
   * Token for reschedule/cancel modes
   */
  token?: string;

  /**
   * Booking context for reschedule/cancel modes
   */
  bookingContext?: BookingActionContext | null;

  /**
   * Hide the header (for preview mode)
   */
  hideHeader?: boolean;

  /**
   * Hide the language selector (for preview mode)
   */
  hideLanguageSelector?: boolean;

  /**
   * Prefetched meeting type from BookingPageContainer (avoids duplicate API call)
   * When provided, skips the meeting type fetch in BookingPageContent
   */
  prefetchedMeetingType?: MeetingType | null;

  /**
   * Direct meeting type injection (for preview mode)
   * When provided, skips API fetching
   */
  previewMeetingType?: MeetingType | null;

  /**
   * Real meeting type ID for preview mode (for fetching related data like service options)
   * In preview mode, previewMeetingType.id is -1, but we need the real ID for API calls
   */
  previewRealMeetingTypeId?: number | null;

  /**
   * Callback for preview mode redirect (shows iframe instead of redirecting page)
   */
  onPreviewRedirect?: (url: string) => void;
}

const BookingPageContent: React.FC<BookingPageContentProps> = ({
  username,
  slug,
  mode,
  token,
  bookingContext,
  hideHeader = false,
  hideLanguageSelector = false,
  prefetchedMeetingType = null,
  previewMeetingType = null,
  previewRealMeetingTypeId = null,
  onPreviewRedirect,
}) => {
  // Get state from booking page context
  const {
    setMeetingType,
    setLoading,
    setError,
    bookingSuccess,
    setBookingSuccess,
    paymentInfo,
    setFormTemplate,
    setLoadingFormTemplate,
  } = useBookingPage();

  // Get state from data provider
  const {
    meetingType: dataProviderMeetingType,
    loadingMeetingType,
    meetingTypeError,
    fetchMeetingType,
    setMeetingType: dataProviderSetMeetingType,
  } = useBookingData();

  // Get i18n for language auto-selection
  const { t, i18n } = useTranslation();

  // Local state for debug mode
  const [isDebugMode, setIsDebugMode] = useState<boolean>(
    process.env.NODE_ENV === "development" &&
      localStorage.getItem("DEBUG_CALENDAR") === "true"
  );

  // State for service booking flow
  // service can be null when there are no service options (direct provider selection)
  const [serviceSelection, setServiceSelection] = useState<{
    service: PublicProduct | null;
    provider: PublicProvider | null;
    isNoPreference: boolean;
    effectiveDuration: number;
    effectivePrice: number;
  } | null>(null);

  // State to control fade-in animation when transitioning to calendar
  const [calendarVisible, setCalendarVisible] = useState(true);
  // State to track returning to provider selection (for back navigation)
  const [returnToProvider, setReturnToProvider] = useState<{
    serviceId: number | null;
    providerId: number | null | undefined;
  } | null>(null);

  // Handler for when service and provider are selected
  // service can be null when there are no service options configured
  const handleServiceProviderSelected = useCallback((
    service: PublicProduct | null,
    provider: PublicProvider | null,
    isNoPreference: boolean,
    effectiveDuration: number,
    effectivePrice: number
  ) => {
    // Hide first so BookingFlow renders hidden
    setCalendarVisible(false);
    // Clear return state since we're moving forward
    setReturnToProvider(null);
    // Set service selection to switch to BookingFlow
    setServiceSelection({ service, provider, isNoPreference, effectiveDuration, effectivePrice });
    // Fade in after React renders the new component (use double RAF for paint)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setCalendarVisible(true);
      });
    });
  }, []);

  // Handler for going back to service/provider selection from calendar
  const handleBackToServiceFlow = useCallback(() => {
    // Fade out BookingFlow first
    setCalendarVisible(false);
    // After fade out, switch back to ServiceBookingFlow at provider step
    setTimeout(() => {
      // Store the selected service ID so we can return to provider selection
      if (serviceSelection) {
        setReturnToProvider({
          serviceId: serviceSelection.service?.id || null,
          providerId: serviceSelection.provider?.userId,
        });
      }
      setServiceSelection(null);
      // Fade in after React renders ServiceBookingFlow
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setCalendarVisible(true);
        });
      });
    }, 200);
  }, [serviceSelection]);

  // Check if this is a service type meeting
  const isServiceType = dataProviderMeetingType?.type === 'service';

  // Check if service selection is complete for service type
  // For duration mode: ServiceBookingFlow skips service selection but still shows provider selection
  const serviceSelectionComplete = !isServiceType || serviceSelection !== null;

  useEffect(() => {
    // Check if debug mode is enabled
    const debugEnabled = localStorage.getItem("DEBUG_CALENDAR") === "true";
    setIsDebugMode(debugEnabled);

    // Listen for debug mode changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "DEBUG_CALENDAR") {
        setIsDebugMode(e.newValue === "true");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Auto-select language for cancel/reschedule modes based on booking language
  useEffect(() => {
    if ((mode === 'cancel' || mode === 'reschedule') && bookingContext?.bookingLanguage) {
      const userSelectedLanguage = sessionStorage.getItem('userChangedLanguage');

      // Only auto-select if user hasn't manually changed language
      if (!userSelectedLanguage) {
        i18n.changeLanguage(bookingContext.bookingLanguage);
      }
    }
  }, [mode, bookingContext, i18n]);

  // Track if we've already initiated the fetch to prevent duplicates
  const hasFetchedRef = React.useRef<string>("");
  // Track if form template has already been fetched to prevent duplicate API calls
  const hasFetchedFormRef = React.useRef<number | null>(null);

  // Fetch meeting type data only if not already fetching and not in preview mode
  useEffect(() => {
    // PREVIEW MODE: Use injected meeting type directly
    if (mode === 'preview' && previewMeetingType) {
      setMeetingType(previewMeetingType);
      dataProviderSetMeetingType(previewMeetingType);
      setLoading(false);
      return;
    }

    // PREFETCH: Use meeting type already fetched by BookingPageContainer (avoids duplicate API call)
    if (prefetchedMeetingType && prefetchedMeetingType.id) {
      hasFetchedRef.current = `${username}-${slug}`;
      setMeetingType(prefetchedMeetingType);
      dataProviderSetMeetingType(prefetchedMeetingType);
      setLoading(false);

      // Still fetch form template (non-blocking, parallel)
      // Uses includeDefault=true so the backend returns the org's default
      // booking template as fallback when no forms are explicitly linked.
      // This avoids calling authenticated endpoints on the public booking page.
      if (prefetchedMeetingType.id && hasFetchedFormRef.current !== prefetchedMeetingType.id) {
        hasFetchedFormRef.current = prefetchedMeetingType.id;
        setLoadingFormTemplate(true);
        formTemplateService.getFormsForMeetingType(prefetchedMeetingType.id, 'pre_booking', true)
          .then((templates) => {
            if (templates && templates.length > 0) {
              setFormTemplate(templates[0]);
            }
          })
          .catch((err) => {
          })
          .finally(() => {
            setLoadingFormTemplate(false);
          });
      }
      return;
    }

    // Skip fetch if we're in action mode - meeting type comes from bookingContext
    if (mode && bookingContext && (mode === 'reschedule' || mode === 'cancel')) {
      // For action modes, set meeting type from booking context with required defaults
      const meetingTypeFromContext = {
        id: parseInt(bookingContext.meetingType.id),
        userId: 0, // Not needed for action modes
        title: bookingContext.meetingType.title,
        description: bookingContext.meetingType.description || "", // Use description from context if available
        duration: bookingContext.meetingType.duration,
        slug: bookingContext.meetingType.slug,
        isActive: true,
        pricing: { type: 'free' as const, amount: 0 }, // Default pricing
        availabilitySchedule: [], // Default empty schedule
        timeZone: bookingContext.timeZone, // Use actual timezone from booking context
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Set meeting type in both page context AND data provider context
      setMeetingType(meetingTypeFromContext);
      dataProviderSetMeetingType(meetingTypeFromContext); // This is crucial for reschedule mode
      setLoading(false);

      // Fetch form template for reschedule mode (needed to render the booking form step)
      const meetingTypeId = parseInt(bookingContext.meetingType.id);
      if (mode === 'reschedule' && meetingTypeId && hasFetchedFormRef.current !== meetingTypeId) {
        hasFetchedFormRef.current = meetingTypeId;
        setLoadingFormTemplate(true);
        formTemplateService.getFormsForMeetingType(meetingTypeId, 'pre_booking', true)
          .then((templates) => {
            if (templates && templates.length > 0) {
              setFormTemplate(templates[0]);
            }
          })
          .catch(() => {})
          .finally(() => {
            setLoadingFormTemplate(false);
          });
      }
      return;
    }

    const fetchKey = `${username}-${slug}`;

    if (username && slug && hasFetchedRef.current !== fetchKey) {
      hasFetchedRef.current = fetchKey;

      // Set loading state in page context
      setLoading(true);

      // Fetch data through the data provider
      fetchMeetingType(username, slug)
        .then((data) => {
          if (data) {
            setMeetingType(data);

            // Fetch form template for this meeting type
            // Uses includeDefault=true so the backend returns the org's default
            // booking template as fallback when no forms are explicitly linked.
            if (data.id && hasFetchedFormRef.current !== data.id) {
              hasFetchedFormRef.current = data.id;
              setLoadingFormTemplate(true);
              formTemplateService.getFormsForMeetingType(data.id, 'pre_booking', true)
                .then((templates) => {
                  if (templates && templates.length > 0) {
                    setFormTemplate(templates[0]);
                  }
                })
                .catch((err) => {
                })
                .finally(() => {
                  setLoadingFormTemplate(false);
                });
            }
          }
          setLoading(false);
        })
        .catch((error) => {
          setError(error.message || "Failed to load meeting type");
          setLoading(false);
        });
    }
  }, [username, slug, mode, bookingContext, prefetchedMeetingType, previewMeetingType, fetchMeetingType, setMeetingType, setLoading, setError, setFormTemplate, setLoadingFormTemplate, dataProviderSetMeetingType]);

  // Sync data provider state with page context
  // Skip when data was already handled by prefetch/preview path above
  useEffect(() => {
    if (prefetchedMeetingType || previewMeetingType) return;

    if (dataProviderMeetingType) {
      setMeetingType(dataProviderMeetingType);
    }

    setLoading(loadingMeetingType);

    if (meetingTypeError) {
      setError(meetingTypeError);
    }
  }, [
    dataProviderMeetingType,
    loadingMeetingType,
    meetingTypeError,
    prefetchedMeetingType,
    previewMeetingType,
    setMeetingType,
    setLoading,
    setError,
  ]);

  // Apply meeting type color theme based on hierarchy: meeting > organization > system default
  useEffect(() => {
    if (dataProviderMeetingType) {
      // System default color constant
      const SYSTEM_DEFAULT_COLOR = '#2E7AA0';

      // Color hierarchy priority:
      // 1. Meeting-specific color (highest priority) - but only if it's a custom color, not the system default
      // 2. Organization-wide brand color
      // 3. System default color (#2E7AA0)

      // Check if meeting type has a custom color (not null/undefined and not the system default)
      const hasCustomMeetingColor = dataProviderMeetingType.brandColor &&
                                    dataProviderMeetingType.brandColor.toUpperCase() !== SYSTEM_DEFAULT_COLOR.toUpperCase();

      const meetingColor = hasCustomMeetingColor
                          ? dataProviderMeetingType.brandColor
                          : (dataProviderMeetingType.user?.organization?.brandColor || SYSTEM_DEFAULT_COLOR);

      applyBrandColor(meetingColor);

      // Cleanup: remove brand color when component unmounts
      return () => {
        removeBrandColor();
      };
    }
  }, [dataProviderMeetingType]);

  // Get cover photos with priority: meetingType > user
  // Returns both desktop and mobile versions
  const getCoverPhotos = () => {
    if (!dataProviderMeetingType) return { desktop: null, mobile: null };

    // Priority: meeting-level cover photos override user-level cover photos
    const desktop = dataProviderMeetingType.coverPhotoUrl ||
                    dataProviderMeetingType.user?.coverPhoto ||
                    null;

    const mobile = dataProviderMeetingType.coverPhotoUrlMobile ||
                   dataProviderMeetingType.user?.coverPhotoMobile ||
                   desktop; // Fallback to desktop if mobile not available

    return { desktop, mobile };
  };

  const coverPhotos = getCoverPhotos();
  const hasCoverPhoto = coverPhotos.desktop || coverPhotos.mobile;

  return (
    <div className="min-h-screen lg:bg-secondary-50">
      {/* Header - only shows for hosts (hidden in preview mode) */}
      {!hideHeader && (
        <BookingPageHeader
          meetingType={dataProviderMeetingType}
          loading={loadingMeetingType}
        />
      )}

      {/* Main Content */}
      <div className="lg:max-w-6xl lg:mx-auto lg:px-8 py-4 md:py-8">
        {/* Error message outside unified container.

            ⚠️ The SIGNAL is rendered, not the string — `meetingTypeError` holds
            whatever the fetch failed with, which is English written for a log
            (or an axios "Request failed with status code 500"). Nothing it can
            hold is more use to a guest than "it did not load", said in their
            own language. */}
        {meetingTypeError && (
          <div className="alert-danger mb-6">
            <p>{t('common:errors.loadingFailed', 'Failed to load data. Please try again.')}</p>
          </div>
        )}

        {/* Unified container with cover photo and content */}
        <div className={`bg-white lg:bg-transparent ${hasCoverPhoto ? 'lg:rounded-lg' : ''}`}>
          {/* Cover Photo Section - Responsive: mobile version on small screens, desktop on large */}
          {hasCoverPhoto && (
            <>
              {/* Mobile Cover Photo (visible only on mobile) */}
              {coverPhotos.mobile && (
                <div className="aspect-banner-mobile lg:rounded-t-lg md:hidden">
                  <img
                    src={coverPhotos.mobile}
                    alt="Cover"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      // Hide the cover photo container if image fails to load
                      const container = e.currentTarget.closest('.aspect-banner-mobile');
                      if (container) {
                        (container as HTMLElement).style.display = 'none';
                      }
                    }}
                  />
                </div>
              )}

              {/* Desktop Cover Photo (visible only on tablet and above) */}
              {coverPhotos.desktop && (
                <div className="aspect-banner lg:rounded-t-lg hidden md:block">
                  <img
                    src={coverPhotos.desktop}
                    alt="Cover"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      // Hide the cover photo container if image fails to load
                      const container = e.currentTarget.closest('.aspect-banner');
                      if (container) {
                        (container as HTMLElement).style.display = 'none';
                      }
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* Main content area */}
          <div className={`${hasCoverPhoto ? 'bg-white lg:rounded-b-lg' : ''}`}>
            {/* Skeleton loader in BookingPageContainer handles initial load, no need for spinner here */}
            {/* Action Mode Content */}
            {mode && mode !== 'booking' && mode !== 'preview' ? (
                mode === 'reschedule' && bookingContext && token ? (
                  !bookingSuccess ? (
                    <div className="booking-container">
                      <BookingFlow
                        meetingType={dataProviderMeetingType}
                        onBookingComplete={() => setBookingSuccess(true)}
                        mode="reschedule"
                        rescheduleToken={token}
                        originalBookingData={{
                          startTime: new Date(bookingContext.startTime),
                          endTime: new Date(bookingContext.endTime),
                          duration: Math.round((new Date(bookingContext.endTime).getTime() - new Date(bookingContext.startTime).getTime()) / 60000),
                          guestName: bookingContext.guestName,
                          guestEmail: bookingContext.guestEmail,
                          notes: bookingContext.notes || '',
                          timeZone: bookingContext.timeZone,
                          formSubmissionData: bookingContext.formSubmissionData || {},
                        }}
                        onPreviewRedirect={onPreviewRedirect}
                      />
                    </div>
                  ) : (
                    <div className="max-w-2xl mx-auto">
                      <BookingConfirmation mode="reschedule" onPreviewRedirect={onPreviewRedirect} />
                    </div>
                  )
                ) : mode === 'cancel' && bookingContext && token ? (
                  <div className="booking-container">
                    <CancelFlow bookingContext={bookingContext} token={token} />
                  </div>
                ) : (
                  <div className="max-w-2xl mx-auto">
                    <div className="alert-danger">
                      <p>Invalid action mode or missing booking data.</p>
                    </div>
                  </div>
                )
              ) : (
                /* Normal Booking Mode Content */
                bookingSuccess ? (
                  <div className="max-w-2xl mx-auto">
                    <BookingConfirmation mode={mode} onPreviewRedirect={onPreviewRedirect} />
                  </div>
                ) : paymentInfo ? (
                  /* Phase 3 — Pay Now: booking row exists in `pending` state,
                     invitee completes Stripe Elements before confirmation. */
                  <div className="max-w-2xl mx-auto py-6">
                    <PaymentScreen />
                  </div>
                ) : (
                  <div className="booking-container">
                    {/* Service Type: Show service/provider selection first */}
                    {isServiceType && !serviceSelectionComplete ? (
                      <div className={`transition-opacity duration-200 ease-in-out ${calendarVisible ? 'opacity-100' : 'opacity-0'}`}>
                        <ServiceBookingFlow
                          meetingType={dataProviderMeetingType!}
                          username={username || ''}
                          slug={slug || ''}
                          meetingTypeId={
                            // In preview mode, use the real meeting type ID (not -1)
                            // For normal booking, use the dataProvider ID
                            mode === 'preview' && previewRealMeetingTypeId
                              ? previewRealMeetingTypeId
                              : dataProviderMeetingType?.id
                          }
                          onServiceProviderSelected={handleServiceProviderSelected}
                          initialStep={returnToProvider ? 'PROVIDER' : 'SERVICE'}
                          previousServiceId={returnToProvider?.serviceId}
                          previousProviderId={returnToProvider?.providerId}
                        />
                      </div>
                    ) : (
                      <div className={`transition-opacity duration-200 ease-in-out ${calendarVisible ? 'opacity-100' : 'opacity-0'}`}>
                        <BookingFlow
                          meetingType={dataProviderMeetingType}
                          username={username}
                          onBookingComplete={() => setBookingSuccess(true)}
                          mode={mode === 'preview' ? 'preview' : undefined}
                          onPreviewRedirect={onPreviewRedirect}
                          serviceSelection={serviceSelection}
                          onBackToServiceFlow={handleBackToServiceFlow}
                          previewRealMeetingTypeId={previewRealMeetingTypeId}
                        />
                      </div>
                    )}
                  </div>
                )
              )
            }
          </div>
        </div>

        {/* Language Selector - Outside body content on grey background (hidden in preview mode) */}
        {!hideLanguageSelector && (
          <div className={`mt-0 mb-4 py-6 lg:py-0 bg-secondary-50 lg:bg-transparent ${
            mode === 'cancel' ? 'px-4 lg:px-6' : 'px-8 lg:px-0'
          }`}>
            {mode === 'cancel' ? (
              <div className="max-w-2xl mx-auto">
                <LanguageSelector />
              </div>
            ) : (
              <LanguageSelector />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPageContent;
